import Image from 'next/image'
import React, { useEffect, useState } from 'react'

import { Icon } from '@/components/Icon/Icon'
import { Button } from '@/shared/components/Button/Button'
import { useLocalStorage } from '@/shared/hooks/useLocalStorage'
import { pxToRem } from '@/shared/styles/themes'
import { faShieldCheck } from '@fortawesome/pro-light-svg-icons/faShieldCheck'
import { faTrashCan } from '@fortawesome/pro-light-svg-icons/faTrashCan'
import { faWandMagicSparkles } from '@fortawesome/pro-light-svg-icons/faWandMagicSparkles'
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  styled,
  Typography,
} from '@mui/material'

const AVATAR_COUNT = 7
const ROTATION_INTERVAL_MS = 1000

const STORAGE_KEY = 'pixieworld_trust_dialog_dismissed'

export const PhotoTrustDialog: React.FC = () => {
  const [persistedDismissed, setPersistedDismissed] = useLocalStorage<boolean>(STORAGE_KEY, false)
  const [open, setOpen] = useState(!persistedDismissed)
  const [dontShowAgain, setDontShowAgain] = useState(false)
  const [activeAvatar, setActiveAvatar] = useState(1)

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveAvatar((prev) => (prev % AVATAR_COUNT) + 1)
    }, ROTATION_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [])

  const handleDismiss = () => {
    if (dontShowAgain) {
      setPersistedDismissed(true)
    }

    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleDismiss} maxWidth="xs" fullWidth>
      <StyledHeroContainer>
        <StyledOriginalPhoto
          src="/images/onboarding/original.png"
          alt="Child photo"
          width={980}
          height={980}
        />
        <StyledArrow
          src="/images/onboarding/arrow.png"
          alt=""
          width={630}
          height={390}
        />
        <StyledAvatarContainer>
          {Array.from({ length: AVATAR_COUNT }, (_, i) => i + 1).map((index) => (
            <StyledAvatarImage
              key={index}
              src={`/images/onboarding/${index}.png`}
              alt={`Avatar style ${index}`}
              width={980}
              height={1136}
              $active={index === activeAvatar}
            />
          ))}
        </StyledAvatarContainer>
      </StyledHeroContainer>
      <DialogContent sx={{ textAlign: 'center', p: pxToRem(32), pt: pxToRem(20) }}>
        <Typography
          variant="h6"
          sx={{ mb: pxToRem(20), fontWeight: 600 }}
        >
          Your child's privacy matters to us
        </Typography>

        <Box sx={{ textAlign: 'left', mb: pxToRem(24) }}>
          <StyledBulletRow>
            <StyledBulletIcon>
              <Icon icon={faWandMagicSparkles} />
            </StyledBulletIcon>
            <Typography variant="body2">
              We create a fun cartoon avatar from your child's photo and use the avatar to illustrate their personalized story.
            </Typography>
          </StyledBulletRow>

          <StyledBulletRow>
            <StyledBulletIcon>
              <Icon icon={faShieldCheck} />
            </StyledBulletIcon>
            <Typography variant="body2">
              Your photos and personal information are kept private and never shared.
            </Typography>
          </StyledBulletRow>

          <StyledBulletRow>
            <StyledBulletIcon>
              <Icon icon={faTrashCan} />
            </StyledBulletIcon>
            <Typography variant="body2">
              You can remove all your data, including stories and avatar images, at any time from your account settings.
            </Typography>
          </StyledBulletRow>
        </Box>

        <Button primary onClick={handleDismiss} fullWidth>
          Got It!
        </Button>

        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
            />
          )}
          label={(
            <Typography variant="caption" color="text.secondary">
              Don&apos;t show this again
            </Typography>
          )}
          sx={{ mt: pxToRem(12) }}
        />

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', mt: pxToRem(16) }}
        >
          Questions? Contact us at{' '}
          <a href="mailto:support@pixie.education" style={{ color: 'inherit' }}>
            support@pixie.education
          </a>
        </Typography>
      </DialogContent>
    </Dialog>
  )
}

const StyledHeroContainer = styled('div')`
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: ${pxToRem(12)};
  background-color: ${(props) => props.theme.palette.custom.brandpink};
  border-radius: ${pxToRem(4)} ${pxToRem(4)} 0 0;
  padding: ${pxToRem(20)} ${pxToRem(24)};
`

const StyledOriginalPhoto = styled(Image)`
  width: ${pxToRem(120)};
  height: ${pxToRem(120)};
  object-fit: contain;
  flex-shrink: 0;
`

const StyledArrow = styled(Image)`
  width: ${pxToRem(48)};
  height: auto;
  flex-shrink: 0;
  object-fit: contain;
  margin-top: ${pxToRem(48)};
`

const StyledAvatarContainer = styled('div')`
  position: relative;
  width: ${pxToRem(120)};
  aspect-ratio: 980 / 1136;
  flex-shrink: 0;
`

const StyledAvatarImage = styled(Image)<{ $active: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
  opacity: ${(props) => (props.$active ? 1 : 0)};
`

const StyledBulletRow = styled('div')`
  display: flex;
  align-items: flex-start;
  gap: ${pxToRem(12)};
  margin-bottom: ${pxToRem(16)};

  &:last-child {
    margin-bottom: 0;
  }
`

const StyledBulletIcon = styled('div')`
  flex-shrink: 0;
  width: ${pxToRem(24)};
  height: ${pxToRem(24)};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => props.theme.palette.primary.main};
  font-size: ${pxToRem(16)};
`
